import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Loader2, Trophy, TrendingUp } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import api from "../services/api";

// All figures come from the backend (the "bucket"). Nothing is hardcoded.

const naira = (kobo) =>
  "₦" +
  Number((kobo || 0) / 100).toLocaleString("en-NG", {
    maximumFractionDigits: 0,
  });

const mins = (m) => (m == null ? "—" : `${m} min`);
const pct = (p) => (p == null ? "—" : `${p}%`);
const secs = (s) => (s == null ? "—" : `${s}s`);
const km = (k) => (k == null ? "—" : `${k} km`);

const RiderPerformance = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Set when the last load failed. With no rows, the page shows the error, not ₦0.
  const [loadError, setLoadError] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/v1/admin/analytics/rider-performance");
      setRows(Array.isArray(data) ? data : data?.data || []);
      setLoadError(null);
    } catch (error) {
      setLoadError(error?.response?.data?.message || "Couldn't load rider performance.");
      if (!silent) {
        toast.error(
          error?.response?.data?.message || "Failed to load rider performance"
        );
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    // Initial load + silent poll so the table stays near real time.
    load();
    const intervalId = setInterval(() => load(true), 30000);
    return () => clearInterval(intervalId);
  }, []);

  const totalNet = rows.reduce((s, r) => s + (r.netEarningsKobo || 0), 0);
  const totalRides = rows.reduce((s, r) => s + (r.completedRides || 0), 0);
  const noData = Boolean(loadError) && rows.length === 0;

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Rider Performance
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Completed rides, net earnings (fare minus Zooma charge), accepted
              rides, acceptance rate, and completion times. Ranked by net
              earnings — top performers first.
            </p>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 max-w-4xl">
            <div className="bg-white dark:bg-black rounded-xl shadow-sm p-5">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                <TrendingUp className="w-4 h-4" /> Total rider earnings (net)
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                {noData ? "—" : naira(totalNet)}
              </div>
            </div>
            <div className="bg-white dark:bg-black rounded-xl shadow-sm p-5">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                <Trophy className="w-4 h-4" /> Completed rides
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                {noData ? "—" : totalRides.toLocaleString()}
              </div>
            </div>
            <div className="bg-white dark:bg-black rounded-xl shadow-sm p-5">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                Active riders
              </div>
              <div className="text-xl font-bold text-gray-900 dark:text-white">
                {noData ? "—" : rows.length}
              </div>
            </div>
          </div>

          {loadError && rows.length > 0 ? (
            <p className="mb-4 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 max-w-4xl">
              {"Couldn't refresh. Showing the last loaded figures."}
            </p>
          ) : null}

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : noData ? (
            <div className="py-20 text-center text-sm text-red-600">
              {"Couldn't load rider performance. "}
              <button type="button" onClick={() => load()} className="underline font-medium">
                Retry
              </button>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-20 text-center text-gray-400">
              No rider data yet.
            </div>
          ) : (
            <div className="bg-white dark:bg-black rounded-xl shadow-sm overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-3 font-medium">#</th>
                    <th className="px-4 py-3 font-medium">Rider</th>
                    <th className="px-4 py-3 font-medium text-right">
                      Completed
                    </th>
                    <th className="px-4 py-3 font-medium text-right">
                      Accepted
                    </th>
                    <th className="px-4 py-3 font-medium text-right">
                      Ignored
                    </th>
                    <th className="px-4 py-3 font-medium text-right">
                      Acceptance
                    </th>
                    <th className="px-4 py-3 font-medium text-right">
                      Avg accept
                    </th>
                    <th className="px-4 py-3 font-medium text-right">
                      Net earnings
                    </th>
                    {/* These two used to be measured from acceptance to
                        PAYMENT CONFIRMATION, so a rider who dropped off in 12
                        minutes and then waited 195 for the customer to pay
                        showed a 208-minute "ride". They now measure arrival at
                        pickup to drop-off — the time actually carrying someone. */}
                    <th className="px-4 py-3 font-medium text-right">
                      Avg ride
                    </th>
                    <th className="px-4 py-3 font-medium text-right">
                      Longest ride
                    </th>
                    {/* The wait that used to be buried in the numbers above.
                        A rider cannot make a customer tap Confirm, so this is
                        shown separately rather than counted against them. */}
                    <th className="px-4 py-3 font-medium text-right">
                      Avg wait for payment
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr
                      key={String(r.riderId)}
                      className="border-b border-gray-50 hover:bg-gray-50 dark:hover:bg-gray-900"
                    >
                      <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {r.riderName || "Unknown"}
                        </div>
                        <div className="text-xs text-gray-400">
                          {r.riderEmail}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">{r.completedRides}</td>
                      <td className="px-4 py-3 text-right">{r.acceptedRides}</td>
                      <td className="px-4 py-3 text-right text-gray-500">
                        {r.ignoredRides ?? 0}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pct(r.acceptanceRate)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {secs(r.avgAcceptanceSec)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">
                        {naira(r.netEarningsKobo)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {mins(r.avgCompletionMin)}
                      </td>
                      <td className="px-4 py-3 text-right" title={km(r.longestRideKm)}>
                        {mins(r.longestRideMin)}
                      </td>
                      <td
                        className={`px-4 py-3 text-right ${
                          (r.avgTimeToPaymentMin ?? 0) > 30
                            ? "text-amber-600"
                            : "text-gray-500"
                        }`}
                        title={
                          r.longestTimeToPaymentMin
                            ? `Longest wait: ${r.longestTimeToPaymentMin} min`
                            : undefined
                        }
                      >
                        {mins(r.avgTimeToPaymentMin)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default RiderPerformance;
